
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Patient } from "@/types/patient.types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  FileText,
  Pill,
  Stethoscope,
  Baby,
  Activity,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { usePatientDeletion } from "../hooks/usePatientDeletion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface EnhancedPatientDetailsProps {
  relation: Patient;
}

export const EnhancedPatientDetails = ({ relation }: EnhancedPatientDetailsProps) => {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState("info");
  const { deleteMedication, deleteJournalEntry, deleteAppointment, deletePatientFile } = usePatientDeletion();

  // Vérifier si c'est un dossier médical
  const { data: patientFile } = useQuery({
    queryKey: ["patient-file", relation.patient?.id],
    queryFn: async () => {
      if (!session?.user?.id || !relation.patient?.id) return null;
      
      const { data, error } = await supabase
        .from("patient_files")
        .select("*")
        .eq("professional_id", session.user.id)
        .or(`id.eq.${relation.patient.id},email.eq.${relation.patient.email}`)
        .maybeSingle();
        
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!session?.user?.id && !!relation.patient?.id,
  });

  // Charger les données de santé du patient
  const { data: medications } = useQuery({
    queryKey: ["patient-medications", relation.patient?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medications")
        .select("*")
        .eq("user_id", relation.patient?.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!relation.patient?.id,
  });

  const { data: journalEntries } = useQuery({
    queryKey: ["patient-journal", relation.patient?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("health_journal")
        .select("*")
        .eq("user_id", relation.patient?.id)
        .order("date", { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
    enabled: !!relation.patient?.id,
  });

  const { data: appointments } = useQuery({
    queryKey: ["patient-appointments", relation.patient?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .eq("user_id", relation.patient?.id)
        .order("date", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!relation.patient?.id,
  });

  const { data: gestationData } = useQuery({
    queryKey: ["patient-gestation", relation.patient?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gestation_tracking")
        .select("*")
        .eq("user_id", relation.patient?.id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!relation.patient?.id,
  });

  const handleDeletePatientFile = async () => {
    if (patientFile?.id) {
      await deletePatientFile.mutateAsync(patientFile.id);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            <User className="h-5 w-5" />
            {patientFile ? `Dossier: ${patientFile.first_name} ${patientFile.last_name}` : relation.patient?.full_name}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={relation.status === 'approved' ? 'default' : 'secondary'}>
              {relation.status === 'approved' ? 'Approuvé' : 'En attente'}
            </Badge>
            {patientFile && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer le dossier
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer le dossier patient</AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer ce dossier patient ? Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeletePatientFile}>
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info">Info</TabsTrigger>
            <TabsTrigger value="medications">Médicaments</TabsTrigger>
            <TabsTrigger value="journal">Journal</TabsTrigger>
            <TabsTrigger value="appointments">RDV</TabsTrigger>
            <TabsTrigger value="gestation">Grossesse</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {patientFile ? (
                <>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Nom:</span>
                    <span>{patientFile.first_name} {patientFile.last_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">N° Dossier:</span>
                    <span>{patientFile.file_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Email:</span>
                    <span>{patientFile.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Téléphone:</span>
                    <span>{patientFile.phone_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Date de naissance:</span>
                    <span>{patientFile.birth_date ? format(new Date(patientFile.birth_date), 'PP', { locale: fr }) : 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Adresse:</span>
                    <span>{patientFile.address || 'Non renseigné'}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Email:</span>
                    <span>{relation.patient?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Téléphone:</span>
                    <span>{relation.patient?.phone_number || 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Pays:</span>
                    <span>{relation.patient?.country || 'Non renseigné'}</span>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="medications" className="space-y-4">
            {medications?.length ? (
              medications.map((med) => (
                <Card key={med.id} className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Pill className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{med.name}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline">{med.dosage}</Badge>
                            <Badge variant="outline">{med.frequency}</Badge>
                            <Badge variant={med.active ? "default" : "secondary"}>
                              {med.active ? "Actif" : "Inactif"}
                            </Badge>
                          </div>
                          {med.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{med.notes}</p>
                          )}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer le médicament</AlertDialogTitle>
                            <AlertDialogDescription>
                              Êtes-vous sûr de vouloir supprimer ce médicament ?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMedication.mutate(med.id)}>
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground">Aucun médicament trouvé</p>
            )}
          </TabsContent>

          <TabsContent value="journal" className="space-y-4">
            {journalEntries?.length ? (
              journalEntries.map((entry) => (
                <Card key={entry.id} className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Activity className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{format(new Date(entry.date), 'PP', { locale: fr })}</p>
                          <Badge variant="outline" className="mt-1">{entry.mood}</Badge>
                          {entry.symptoms && entry.symptoms.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium">Symptômes:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {entry.symptoms.map((symptom, index) => (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {symptom}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {entry.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{entry.notes}</p>
                          )}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer l'entrée</AlertDialogTitle>
                            <AlertDialogDescription>
                              Êtes-vous sûr de vouloir supprimer cette entrée du journal ?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteJournalEntry.mutate(entry.id)}>
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground">Aucune entrée de journal trouvée</p>
            )}
          </TabsContent>

          <TabsContent value="appointments" className="space-y-4">
            {appointments?.length ? (
              appointments.map((appointment) => (
                <Card key={appointment.id} className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Stethoscope className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{appointment.doctor_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(appointment.date), 'PPp', { locale: fr })}
                          </p>
                          <p className="text-sm text-muted-foreground">{appointment.location}</p>
                          {appointment.notes && (
                            <p className="text-sm text-muted-foreground mt-2">{appointment.notes}</p>
                          )}
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer le rendez-vous</AlertDialogTitle>
                            <AlertDialogDescription>
                              Êtes-vous sûr de vouloir supprimer ce rendez-vous ?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteAppointment.mutate(appointment.id)}>
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground">Aucun rendez-vous trouvé</p>
            )}
          </TabsContent>

          <TabsContent value="gestation" className="space-y-4">
            {gestationData?.length ? (
              gestationData.map((gestation) => (
                <Card key={gestation.id} className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Baby className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">Suivi de grossesse</p>
                        <p className="text-sm text-muted-foreground">
                          Début: {format(new Date(gestation.pregnancy_start_date), 'PP', { locale: fr })}
                        </p>
                        {gestation.last_checkup_date && (
                          <p className="text-sm text-muted-foreground">
                            Dernier contrôle: {format(new Date(gestation.last_checkup_date), 'PP', { locale: fr })}
                          </p>
                        )}
                        {gestation.next_checkup_date && (
                          <p className="text-sm text-muted-foreground">
                            Prochain contrôle: {format(new Date(gestation.next_checkup_date), 'PP', { locale: fr })}
                          </p>
                        )}
                        {gestation.notes && (
                          <p className="text-sm text-muted-foreground mt-2">{gestation.notes}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground">Aucun suivi de grossesse trouvé</p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
